import React, { useLayoutEffect } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, CardView, CollapsableContainer, rem, Spacer, withAnimatedNavigationHeader } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import Squircle from 'design-system/Squircle';
import Text from 'design-system/Text';
import IconEmmaWallet from 'design-system/icons/IconEmmaWallet';
import IconTooltip from 'design-system/icons/IconTooltip';
import { SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import IconStatusUp from 'design-system/icons/IconStatusUp';
import { IconCoins, IconRectangleListSolid } from 'design-system/icons';
import { IconWalletCoin } from 'design-system/icons/IconWalletCoin';
import { IconTimeCircle } from 'design-system/icons/IconTimeCircle';
import { useAppSelector } from 'store/hooks';
import { selectUserTier } from 'features/premium/selectors/tier';
import { formatAmount } from 'utils/formatting';
import SetupInvestmentAccountButton from '../components/SetupInvestmentAccountButton';
import { AccountTypeDescription, AccountTypeDisplay, AccountTypeDisplayLong } from '../constants';
import { isWeb } from '../../../constants';
import { selectSecclAccountAER } from '../reducers/selectors';
const GIA_FEATURES = (secclAccountAER) => [
    {
        icon: <IconStatusUp gradientKey="darkPink"/>,
        title: 'No investment limit',
        description: 'Put in as much money to work as you like. No annual cap, no restrictions',
    },
    {
        icon: <IconCoins width={16} height={16} gradientKey="skyBlue"/>,
        title: 'Earn on cash',
        description: secclAccountAER ? (<Text TextThin-14 Secondary>
          Earn{' '}
          <Text NumbersThin-14 Secondary>
            {secclAccountAER?.toFixed(2)}%
          </Text>{' '}
          AER on money held in your GIA before it’s invested
        </Text>) : ('Earn interest on money held in your GIA before it’s invested'),
    },
    {
        icon: <IconRectangleListSolid gradientKey="skyBlue"/>,
        title: 'Choose how you invest',
        description: 'Go hands-off with ready-made funds, or take control by building your own portfolio with do-it-yourself funds',
    },
    {
        icon: <IconWalletCoin gradientKey="yellow"/>,
        title: (<Text Text-16>
          Start from <Text Numbers-16>£1</Text>
        </Text>),
        description: 'No large upfront deposit needed. Begin investing at any amount',
    },
];
const ISA_FEATURES = (secclAccountAER) => [
    {
        icon: <IconStatusUp gradientKey="darkPink"/>,
        title: 'Tax-free growth',
        description: (<Text TextThin-14 Secondary>
          Invest up to{' '}
          <Text NumbersThin-14 Secondary>
            £20,000
          </Text>{' '}
          each tax year and pay no Capital Gains or Income Tax on returns
        </Text>),
    },
    {
        icon: <IconCoins width={16} height={16} gradientKey="skyBlue"/>,
        title: 'Earn on cash',
        description: secclAccountAER ? (<Text TextThin-14 Secondary>
          Earn{' '}
          <Text NumbersThin-14 Secondary>
            {secclAccountAER?.toFixed(2)}%
          </Text>{' '}
          AER on money held in your ISA before it’s invested
        </Text>) : ('Earn interest on money held in your ISA before it’s invested'),
    },
    {
        icon: <IconRectangleListSolid gradientKey="skyBlue"/>,
        title: 'Choose how you invest',
        description: 'Go hands-off with ready-made funds, or take control by building your own portfolio with do-it-yourself funds',
    },
    {
        icon: <IconTimeCircle gradientKey="yellow"/>,
        title: 'Flexible access',
        description: 'Withdraw and replace money in the same tax year without using your allowance',
    },
];
const JISA_FEATURES = [
    {
        icon: <IconStatusUp gradientKey="darkPink"/>,
        title: 'Invest in your child’s future',
        description: (<Text TextThin-14 Secondary>
        Contribute up to{' '}
        <Text NumbersThin-14 Secondary>
          £9,000
        </Text>{' '}
        each tax year, without affecting your own ISA allowance
      </Text>),
    },
    {
        icon: <IconCoins width={16} height={16} gradientKey="skyBlue"/>,
        title: 'Earn on cash',
        description: 'Earn a competitive variable rate on money held in your Junior ISA before it’s invested',
    },
    {
        icon: <IconRectangleListSolid gradientKey="skyBlue"/>,
        title: 'Choose how you invest',
        description: 'Go hands-off with ready-made funds, or take control by building your own portfolio with do-it-yourself funds',
    },
    {
        icon: <IconTimeCircle gradientKey="yellow"/>,
        title: 'Becomes an ISA at 18',
        description: 'Your child gets full control, with investments continuing tax-free',
    },
];
const INTRO_CONTENT = (secclAccountAER) => ({
    GIA: {
        subTitle: `${AccountTypeDescription.GIA}. Capital at risk`,
        features: GIA_FEATURES(secclAccountAER),
        footerText: (<Text TextThin-14 Secondary>
          Capital Gains Tax may apply if your total profits are over{' '}
          <Text NumbersThin-14 Secondary>
            £3,000
          </Text>{' '}
          in a tax year. Tax treatment depends on your individual circumstances.
        </Text>),
    },
    ISA: {
        subTitle: <>{AccountTypeDescription.ISA}. Capital at risk</>,
        features: ISA_FEATURES(secclAccountAER),
        footerText: 'Tax treatment depends on your individual circumstances and may change in future.',
    },
    JISA: {
        subTitle: <>{AccountTypeDescription.JISA}. Capital at risk</>,
        features: JISA_FEATURES,
        footerText: 'If you have more than one child, you can open a separate Junior ISA for each of them.',
    },
});
const IntroducingInvestmentAccountScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const { accountType } = route.params;
    const colors = useColors();
    const tier = useAppSelector(selectUserTier);
    const tierAumFeesMap = useAppSelector((state) => state.invest.tierSecclAUMFeesMap);
    const secclAccountAER = useAppSelector((store) => selectSecclAccountAER(store, accountType));
    const { subTitle, features, footerText } = INTRO_CONTENT(secclAccountAER)[accountType];
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, [
        paddingHorizontalStyle,
        styles.contentContainer,
    ]);
    return (<AppView type="secondary" withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <View style={styles.wrapper}>
              <CardView style={styles.cardView}>
                <View style={styles.titleContainer}>
                  <Text Text-20 style={styles.title}>
                    {`Introducing ${AccountTypeDisplayLong[accountType]} (${AccountTypeDisplay[accountType]})`}
                  </Text>
                  <Spacer h={4}/>
                  <Text TextThin-14 Secondary style={styles.subTitle}>
                    {subTitle}
                  </Text>
                </View>
                {features.map((feature, index) => (<View key={index} style={styles.itemContainer}>
                    <Squircle style={styles.iconContainer} color={colors.background.dark}>
                      {feature.icon}
                    </Squircle>
                    <View style={styles.textContainer}>
                      <Text Text-16>{feature.title}</Text>
                      <Text TextThin-14 Secondary>
                        {feature.description}
                      </Text>
                    </View>
                  </View>))}
              </CardView>
              <CollapsableContainer title="Fees" style={styles.feesSection} isCollapsedByDefault>
                <View style={styles.collapsibleContent}>
                  <Text TextThin-14 Secondary>
                    Emma funds are low cost and managed by leading providers like BlackRock, Vanguard, and Fidelity.
                  </Text>
                  {!!tierAumFeesMap && (<>
                      <Spacer h={10}/>
                      <Text TextThin-14 Secondary>
                        The AUM fee depends on your Emma plan. Your current AUM fee is{' '}
                        <Text NumbersThin-14 Secondary>
                          {tierAumFeesMap[tier]}%
                        </Text>
                        . You will pay a total of{' '}
                        <Text NumbersThin-14 Secondary>
                          {formatAmount(Number(tierAumFeesMap[tier]) * 50, 'GBP', 2)}
                        </Text>{' '}
                        a year if you invest{' '}
                        <Text NumbersThin-14 Secondary>
                          £5,000
                        </Text>
                        .
                      </Text>
                    </>)}
                  <Spacer h={10}/>
                  <Text TextThin-14 Secondary>
                    There are no trading charges.
                  </Text>
                  <Spacer h={10}/>
                  <Text TextThin-14 Secondary>
                    Each fund also has its own ongoing charges figure (OCF), set by the fund manager and built into the
                    fund’s price. This varies by fund.
                  </Text>
                </View>
              </CollapsableContainer>

              <CardView noMarginTop withPadding>
                <View style={styles.row}>
                  <IconTooltip colorKey="secondary"/>
                  <Spacer.W8 />
                  <View style={styles.flex}>
                    <Text TextThin-14 Secondary>
                      {footerText}
                    </Text>
                  </View>
                </View>
              </CardView>
              <View style={styles.topIconView}>
                <View style={[styles.topIconBorder, { borderColor: colors.background.dark }]}>
                  <IconEmmaWallet size={rem(52)}/>
                </View>
              </View>
            </View>
          </Animated.ScrollView>
          <SetupInvestmentAccountButton floating brand accountType={accountType}/>
        </AppView>);
}, ({ colors }) => ({
    title: '',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default IntroducingInvestmentAccountScreen;
const styleSet = createStyleSheets((colors) => ({
    wrapper: {
        paddingTop: rem(16),
    },
    cardView: {
        position: 'relative',
        overflow: 'visible',
        marginTop: rem(22),
    },
    titleContainer: {
        paddingTop: rem(38),
        borderBottomWidth: 1,
        borderBottomColor: colors.background.dark,
        paddingHorizontal: rem(16),
    },
    title: {
        marginBottom: rem(4),
        textAlign: 'center',
    },
    subTitle: {
        marginBottom: rem(24),
        textAlign: 'center',
    },
    itemContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: rem(16),
        width: '100%',
    },
    iconContainer: {
        marginRight: rem(16),
    },
    textContainer: {
        flex: 1,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    topIconView: {
        left: 0,
        right: 0,
        top: rem(6),
        position: 'absolute',
        alignItems: 'center',
    },
    topIconBorder: {
        borderWidth: rem(6),
        borderRadius: rem(52),
        overflow: 'hidden',
    },
    feesSection: {
        marginBottom: rem(16),
        marginHorizontal: rem(16),
    },
    collapsibleContent: {
        padding: rem(16),
    },
    contentContainer: {
        paddingTop: rem(16),
    },
    flex: {
        flex: 1,
    },
}));
